/**
 * 保留首次加载的原始url信息，方便一下功能的实现：
 * - 日志记录时携带原始分享参数
 * - 二次分享时再次携带原始分享参数
 * - 指定接口调用时传递原始分享参数
 */

import queryString from 'query-string';
import env, { isInWechat, isInZhuzher, isMiniProgram } from '@/utils/env';
import { apiPrefix } from '@/services/common';
import logUpload from './logUpload';
import logStorage from './logStorage';
import { SHARE_UID } from '@/utils/constants';

interface ISetting {
  config: {
    // 日志记录
    logs?: Array<{
      // 日志基础信息
      baseInfo: {
        statistics_name?: string;
      };
      // 匹配字段, '与' 关系，需要完全匹配
      mapKeywords: Array<string>;
      // 需要打日志的接口调用
      logApis?: Array<{
        // url链接
        url: string;
        // 调用方式
        method: 'post' | 'get';
        // 日志基础信息
        baseInfo?: Record<string, any> & {
          // 日志类型
          item_style: string;
        };
      }>;
    }>;
    // 跳转到外部链接需要把原始字段拷贝出去, 主要用于处理新开webview的情况
    outwardUrls?: Array<{
      url: string;
      // 匹配字段, '或' 关系，需要完全匹配
      mapKeywords: Array<string>;
    }>;
    // api调用 params参数指定
    apis?: Array<{
      // url链接
      url: string;
      // 调用方式
      method: 'post' | 'get';
      // 日志匹配关键字,‘或’关系
      mapKeywords: Array<string>;
    }>;
    forbidFirstLoadUrls: Array<string>;
  };
}
const setting: ISetting = {
  // 当前配置
  config: {
    logs: [
      {
        baseInfo: {
          statistics_name: 'first_load',
        },
        mapKeywords: ['pageMark'],
        logApis: [
          {
            url: `${env.FD_API_HOST}/pluto/api/coupon/v1/page/achieve`,
            method: 'post',
            baseInfo: {
              item_style: 'get_coupon',
            },
          },
        ],
      },
      {
        baseInfo: {
          statistics_name: 'first_load',
        },
        mapKeywords: ['moduleMark'],
      },
    ],
    outwardUrls: [
      {
        url: env.FG_HOST,
        mapKeywords: [
          'originUrl',
          'pageMark',
          'sessionId',
          'moduleMark',
          'trackId',
          'share_code',
          'share_uid',
          'first_share_uid',
          'source',
          'sourceId',
          'share_channel',
          'extShareId',
          'from',
        ],
      },
      {
        url: env.NEIGHBOUR_API_HOST,
        mapKeywords: [
          'originUrl',
          'pageMark',
          'sessionId',
          'moduleMark',
          'trackId',
          'share_code',
          'share_uid',
          'first_share_uid',
          'source',
          'sourceId',
          'share_channel',
          'miniScene',
          'openGid',
          'unionId',
          'latitude',
          'longitude',
          'extShareId',
          'from',
          'shareUid',
        ],
      },
      {
        url: env.BLACKPEARL_HOST,
        mapKeywords: [
          'originUrl',
          'pageMark',
          'sessionId',
          'moduleMark',
          'trackId',
          'share_code',
          'share_uid',
          'first_share_uid',
          'source',
          'sourceId',
          'share_channel',
          'extShareId',
          'from',
          'shareUid',
        ],
      },
    ],
    apis: [
      {
        url: `${env.FD_API_HOST}/pluto/api/coupon/v1/page/achieve`,
        method: 'post',
        mapKeywords: ['pageMark'],
      },
      {
        url: `${apiPrefix}/interfaces/order/create`,
        method: 'post',
        mapKeywords: [
          'pageMark',
          'moduleMark',
          'share_code',
          'share_uid',
          'first_share_uid',
          'source',
          'sourceId',
          'share_channel',
          'originUrl',
          'extShareId',
          'from',
          'shareUid',
        ],
      },
      {
        url: `${apiPrefix}/interfaces/order/seckill/create`,
        method: 'post',
        mapKeywords: [
          'pageMark',
          'moduleMark',
          'share_code',
          'share_uid',
          'first_share_uid',
          'source',
          'sourceId',
          'share_channel',
          'originUrl',
          'extShareId',
          'from',
          'shareUid',
        ],
      },
      {
        url: `${apiPrefix}/interfaces/share/create`,
        method: 'get',
        mapKeywords: [
          'share_code',
          'share_uid',
          'first_share_uid',
          'source',
          'sourceId',
          'share_channel',
          'originUrl',
          'extShareId',
          'from',
          'shareUid',
        ],
      },
    ],
    forbidFirstLoadUrls: ['/nova/seckill-detail'],
  },
};

function getLogConfigAndOriginQuerys(
  type: 'mapKeywords' | 'logApis',
  apiOrUrl?: string,
  method?: string,
) {
  const initQuerys = queryString.parseUrl(logStorage.initUrl).query;
  let logConfig: NonNullable<ISetting['config']['logs']>[0] | undefined;

  if (type === 'mapKeywords') {
    // 取匹配的第一条配置
    logConfig = setting.config.logs?.find(log => {
      // 字段需要完全匹配
      return log.mapKeywords.every(keyword => initQuerys[keyword]);
    });
  }

  if (type === 'logApis') {
    // 取匹配的第一条配置
    logConfig = setting.config.logs?.find(log => {
      // 字段需要完全匹配
      return log.logApis?.find(
        querys =>
          querys.url === apiOrUrl && querys.method === method?.toLowerCase(),
      );
    });
  }
  return {
    initQuerys,
    logConfig,
  };
}

// 返回originUrl与priortyOriginQuery合并的query对象，其中优先取priortyOriginQuery中的字段值
export function getMixQuerys() {
  const initQuerys = queryString.parseUrl(logStorage.initUrl).query;
  return {
    ...initQuerys,
    ...logStorage.globalInfo,
  };
}

const getPlatformFrom = () => {
  const isHomeMini = sessionStorage.getItem('FROM_GET_HOME_MINI');
  enum pt {
    // 到家小程序
    MINI_HOME = 'h5_get_home_mini',
    // 友邻小程序
    MINI_YOU_LIN = 'h5_you_lin_mini',
    // 住这儿 APP
    ZZR_APP = 'h5_zzr_app',
    // 微信
    WECHAT = 'h5_wechat',
    // 其他
    OTHER = 'h5_other',
  }
  if (isMiniProgram) {
    if (isHomeMini) {
      return pt.MINI_HOME;
    }
    return pt.MINI_YOU_LIN;
  }
  if (isInZhuzher) {
    return pt.ZZR_APP;
  }
  if (isInWechat) {
    return pt.WECHAT;
  }
  return pt.OTHER;
};

// 首次加载日志记录
function logFirstLoad() {
  const { initQuerys, logConfig } = getLogConfigAndOriginQuerys('mapKeywords');
  // 给页面事件日志系统设置pageMark标记
  logStorage.setPageMark((initQuerys.pageMark as string) || undefined);
  // 给页面事件日志系统设置moduleMark标记
  logStorage.setModuleMark((initQuerys.moduleMark as string) || undefined);

  if (isMiniProgram) {
    // 初始化场景值，群聊id
    logStorage.setGlobalInfo({
      miniScene: initQuerys.miniScene || undefined,
      openGid: initQuerys.openGid || undefined,
      unionId: initQuerys.unionId || undefined,
      latitude: initQuerys.latitude || undefined,
      longitude: initQuerys.longitude || undefined,
    });
  }
  // 给页面记录来源URL标记
  logStorage.setOriginUrl(
    initQuerys.originUrl && typeof initQuerys.originUrl === 'string'
      ? decodeURIComponent(initQuerys.originUrl)
      : logStorage.initUrl,
  );
  if ('sessionId' in initQuerys) {
    // 给页面事件日志系统设置uuid标记
    logStorage.setSessionId((initQuerys.sessionId as string) || undefined);
  }
  if ('trackId' in initQuerys) {
    // 给页面事件日志系统设置uuid标记
    logStorage.setTrackId((initQuerys.trackId as string) || undefined);
  }

  if ('shareUid' in initQuerys && initQuerys?.shareUid) {
    logStorage.setShareUid(SHARE_UID, initQuerys?.shareUid as string);
  }

  // 没有达标记录，或者url标识了不是入口url，则不需要打日志
  if (!logConfig || initQuerys.no_entry) {
    return;
  }

  const querys: Record<string, any> = {
    item_style: 'page_load',
  };
  logConfig.mapKeywords?.forEach(keyword => {
    querys[keyword] = initQuerys[keyword];
  });

  if (
    setting.config.forbidFirstLoadUrls.find(url => location.pathname.match(url))
  ) {
    return;
  }

  // 如果不使用异步，会导致request中间件失效
  setTimeout(() => {
    logUpload({
      ...logConfig.baseInfo,
      ...querys,
    });
  }, 1000);
}

// 记录首次加载url
export const initOriginUrl = () => {
  // 记录入口URL
  logStorage.setInitUrl(location.href);
  // 记录来源URL
  logFirstLoad();
};

// 返回originUrl
export const getOriginUrl = () => {
  return logStorage.originUrl;
};

// 设置高优先级的query字段
export const setPriortyOriginQuery = (data?: Record<string, any>) => {
  logStorage.setGlobalInfo(data || {});
};

// 匹配URL，跳转带上指定参数
export const getMarkingUrl = (outwardUrl: string) => {
  const mixQuerys = getMixQuerys();
  const config = setting.config.outwardUrls?.find(outward => {
    return outwardUrl?.match(outward.url);
  });

  if (!config) {
    return outwardUrl;
  }

  const data = queryString.parseUrl(outwardUrl, {
    parseFragmentIdentifier: true,
  });
  console.log('queryString', outwardUrl, data);

  const querys: Record<string, any> = {
    no_entry: true, // 标识后续的url不是入口
  };

  config.mapKeywords?.forEach(keyword => {
    querys[keyword] = mixQuerys[keyword];
  });

  return queryString.stringifyUrl({
    ...data,
    // 如果存在同名的字段，优先取现有url的，而非originUrl的
    query: { ...querys, ...data.query },
  });
};

// h5 跳转小程序仅需带上moduleMark，其他分享参数小程序都有
export const getModuleMarkUrl = (outwardUrl: string) => {
  const mixQuerys = getMixQuerys();
  const data = queryString.parseUrl(outwardUrl, {
    parseFragmentIdentifier: true,
  });

  const querys: Record<string, any> = {
    no_entry: true, // 标识后续的url不是入口
    moduleMark: mixQuerys.moduleMark,
    trackId: mixQuerys.trackId,
  };

  return queryString.stringifyUrl({
    ...data,
    // 如果存在同名的字段，优先取现有url的，而非originUrl的
    query: { ...querys, ...data.query },
  });
};

// 小程序url分享带上pageMark, moduleMark
export const getShareUrl = (outwardUrl: string) => {
  const data = queryString.parseUrl(outwardUrl, {
    parseFragmentIdentifier: true,
  });

  const url = queryString.stringifyUrl({
    ...data,
    query: {
      pageMark: logStorage.pageMark,
      moduleMark: logStorage.moduleMark,
      ...data.query,
    },
  });

  console.log('share url', url);

  return url;
};

// 接口请求触发日志
export const logMarkingApi = (apiUrl: string, method?: string) => {
  const { initQuerys, logConfig } = getLogConfigAndOriginQuerys(
    'logApis',
    apiUrl,
    method,
  );
  const logApi = logConfig?.logApis?.find(
    item =>
      item.url === apiUrl &&
      item.method.toLowerCase() === method?.toLowerCase(),
  );

  if (!logConfig || !logApi) {
    return;
  }

  const querys: Record<string, any> = {
    item_style: 'page_load',
  };
  logConfig.mapKeywords?.forEach(keyword => {
    querys[keyword] = initQuerys[keyword];
  });

  setTimeout(() => {
    logUpload({
      ...logConfig.baseInfo,
      ...querys,
      ...logApi.baseInfo,
    });
  }, 1000);
};

// 给接口请求添加额外数据
export const getMarkingApiData = (apiUrl: string, method?: string) => {
  const mixQuerys = getMixQuerys();
  const configApi = setting.config.apis?.find(
    item =>
      apiUrl.match(item.url) &&
      item.method.toLowerCase() === method?.toLowerCase(),
  );
  // const configApi = setting.config.apis?.find(item => {
  //   const matchesUrl = apiUrl.match(item.url);
  //   const matchesMethod = method?.toLowerCase() === item.method.toLowerCase();
  //   return matchesUrl && matchesMethod;
  // });
  if (!configApi) {
    return null;
  }

  const querys: Record<string, any> = {};

  configApi.mapKeywords?.forEach(keyword => {
    if (keyword === 'from') {
      querys[keyword] = getPlatformFrom();
    } else if (keyword === 'shareUid') {
      querys.inviteUid = mixQuerys[keyword]?.toString?.() || undefined;
      querys[keyword] = mixQuerys[keyword]?.toString?.() || undefined;
    } else {
      querys[keyword] = mixQuerys[keyword]?.toString?.() || undefined; // 参数忽略空字符串、、
    }
  });

  return querys;
};
